/**
 * WorkflowsPage — cross-vendor view of every workflow run Opus has launched.
 *
 * Refactored to use <DataTable> (extracted from the site dashboard work).
 * The day-grouped timeline was nice but didn't compose with pagination;
 * a sortable single-table layout with "triggered at" as a sortable column
 * gives the same chronological narrative AND scales to thousands of runs.
 */
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  KeyRound,
  RefreshCw,
  RotateCw,
  Sparkles,
  XCircle,
  XOctagon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { KpiCard } from "@/components/ui/kpi-card";
import { Progress } from "@/components/ui/progress";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  PanelSkeleton,
  Skeleton,
  TableSkeleton,
} from "@/components/ui/skeleton";
import { useWorkflowRuns, type WorkflowFilters } from "@/stores/workflow-runs";
import { useAuth } from "@/stores/auth";
import type { WorkflowRun, WorkflowRunState } from "@/api/schemas/workflows";
import { AiExplain } from "@/components/app/ai-explain";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/* ---------- helpers ---------- */

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function formatDuration(ms?: number): string {
  if (ms === undefined) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000)
    return `${Math.round(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
  return `${Math.round(ms / 3_600_000)}h ${Math.round((ms % 3_600_000) / 60_000)}m`;
}

function stateStyle(s: WorkflowRunState): {
  pill: "ok" | "info" | "warn" | "err" | "neutral";
  label: string;
  icon: React.ReactNode;
} {
  switch (s) {
    case "success":
      return {
        pill: "ok",
        label: "succeeded",
        icon: <CheckCircle2 size={14} />,
      };
    case "running":
      return { pill: "info", label: "running", icon: <Clock size={14} /> };
    case "queued":
      return { pill: "warn", label: "queued", icon: <Clock size={14} /> };
    case "failed":
      return { pill: "err", label: "failed", icon: <XCircle size={14} /> };
    case "cancelled":
      return {
        pill: "neutral",
        label: "cancelled",
        icon: <XOctagon size={14} />,
      };
  }
}

/** Sort key: open states first (running/queued), then by triggered time descending. */
const STATE_RANK: Record<WorkflowRunState, number> = {
  running: 0,
  queued: 1,
  failed: 2,
  cancelled: 3,
  success: 4,
};

const ALL_STATES: WorkflowRunState[] = [
  "queued",
  "running",
  "success",
  "failed",
  "cancelled",
];
const ALL_VENDORS: Array<"meraki" | "aruba" | "shasta" | "system"> = [
  "meraki",
  "aruba",
  "shasta",
  "system",
];
const WORKFLOW_TABLE_COLS = "150px minmax(280px,1.6fr) 100px 140px 150px 120px";
const isInitialLoading = (status: string, dataLength: number) =>
  status === "loading" && dataLength === 0;

function buildWorkflowColumns({
  cancel,
  retry,
}: {
  cancel: (id: string) => Promise<void>;
  retry: (id: string) => Promise<void>;
}): DataTableColumn<WorkflowRun>[] {
  return [
    {
      key: "state",
      label: "State",
      width: "150px",
      sortable: true,
      sortValue: (r) => STATE_RANK[r.state],
      render: (r) => {
        const sty = stateStyle(r.state);
        return (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex h-6 w-6 items-center justify-center rounded-full",
                sty.pill === "ok" && "bg-mint/15 text-mint",
                sty.pill === "info" && "bg-violet/15 text-violet",
                sty.pill === "warn" && "bg-peach/15 text-peach",
                sty.pill === "err" && "bg-rose/15 text-rose",
                sty.pill === "neutral" && "bg-line/30 text-fg-mute",
              )}
            >
              {sty.icon}
            </span>
            <Badge variant={sty.pill} size="sm">
              {sty.label}
            </Badge>
          </div>
        );
      },
    },
    {
      key: "workflow",
      label: "Workflow",
      width: "minmax(280px, 1.6fr)",
      sortable: true,
      sortValue: (r) => r.workflowName,
      render: (r) => (
        <div>
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-semibold text-fg">
              {r.workflowName}
            </div>
            {r.scheduled ? (
              <Badge variant="neutral" size="sm">
                <CalendarClock size={10} />
                scheduled
              </Badge>
            ) : null}
          </div>
          <div className="mt-0.5 font-mono text-[10.5px] text-fg-mute">
            {r.workflowSlug} · target <b className="text-fg-dim">{r.target}</b>
          </div>
          {r.state === "running" ? (
            <div className="mt-1.5 max-w-md">
              <Progress value={r.progressPct} hint={r.message} />
            </div>
          ) : r.message ? (
            <div
              className={cn(
                "mt-1 text-[11px]",
                r.state === "failed" ? "text-rose" : "text-fg-dim",
              )}
            >
              {r.message}
            </div>
          ) : null}
          {r.state === "failed" ? (
            <div className="mt-1.5 max-w-md">
              <AiExplain
                kind="workflow-run-failure"
                context={r as unknown as Record<string, unknown>}
                variant="compact"
                label="Why did this fail?"
              />
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "vendor",
      label: "Vendor",
      width: "100px",
      sortable: true,
      sortValue: (r) => r.vendor,
      render: (r) => (
        <Badge variant="neutral" size="sm">
          {r.vendor}
        </Badge>
      ),
    },
    {
      key: "triggeredBy",
      label: "Triggered by",
      width: "140px",
      sortable: true,
      sortValue: (r) => r.triggeredBy,
      render: (r) => (
        <span className="text-xs text-fg-dim">{r.triggeredBy}</span>
      ),
    },
    {
      key: "triggeredAt",
      label: "When",
      width: "150px",
      sortable: true,
      sortValue: (r) => -new Date(r.triggeredAt).getTime(),
      render: (r) => (
        <div className="text-xs">
          <div className="text-fg-dim">{timeAgo(r.triggeredAt)}</div>
          {r.durationMs !== undefined ? (
            <div className="font-mono text-[10.5px] text-fg-mute">
              took {formatDuration(r.durationMs)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "120px",
      align: "right",
      render: (r) => {
        if (r.state === "running" || r.state === "queued") {
          return (
            <Button size="sm" variant="ghost" onClick={() => void cancel(r.id)}>
              <XOctagon size={13} />
              Cancel
            </Button>
          );
        }
        if (r.state === "failed" || r.state === "cancelled") {
          return (
            <Button size="sm" variant="ghost" onClick={() => void retry(r.id)}>
              <RotateCw size={13} />
              Retry
            </Button>
          );
        }
        return null;
      },
    },
  ];
}

function WorkflowFilterBar({
  filters,
  toggleState,
  toggleVendor,
  setFilters,
  resetFilters,
}: {
  filters: { states: WorkflowRunState[]; vendors: string[]; hideScheduled: boolean };
  toggleState: (s: WorkflowRunState) => void;
  toggleVendor: (v: (typeof ALL_VENDORS)[number]) => void;
  setFilters: (patch: Partial<WorkflowFilters>) => void;
  resetFilters: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            State
          </span>
          {ALL_STATES.map((s) => {
            const active = filters.states.includes(s);
            const sty = stateStyle(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleState(s)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {sty.icon}
                {sty.label}
              </button>
            );
          })}
          <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Vendor
          </span>
          {ALL_VENDORS.map((v) => {
            const active = filters.vendors.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggleVendor(v)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {v}
              </button>
            );
          })}
          <Button
            size="sm"
            variant={filters.hideScheduled ? "primary" : "ghost"}
            onClick={() => setFilters({ hideScheduled: !filters.hideScheduled })}
            className="ml-auto"
          >
            <CalendarClock size={13} />
            Hide scheduled
          </Button>
          <Button size="sm" variant="ghost" onClick={() => resetFilters()}>
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================ */
/* PAGE                                         */
/* ============================================ */

export function WorkflowsPage() {
  const animatedRootRef = useUiAutoAnimate();

  const {
    data,
    total,
    filters,
    status,
    error,
    load,
    setFilters,
    resetFilters,
    cancel,
    retry,
  } = useWorkflowRuns();
  const user = useAuth((s) => s.user);
  // Filters are considered "applied" when the user has narrowed the
  // scope at least once. Used so the no-keys empty-state card only
  // shows when there's nothing AND nothing's been filtered (vs. the
  // "your filters returned 0 results" message which is also legitimate).
  const hasActiveFilters =
    filters.query.length > 0 ||
    filters.states.length > 0 ||
    filters.vendors.length > 0 ||
    filters.hideScheduled;
  const showNoKeysEmptyState =
    !!user &&
    user.merakiKeyConfigured === false &&
    data.length === 0 &&
    !error &&
    status === "ready" &&
    !hasActiveFilters;

  // Initial load only — chip filters trigger their own reload via the store.
  useEffect(() => {
    void load();
  }, [load]);

  function toggleState(s: WorkflowRunState) {
    const has = filters.states.includes(s);
    setFilters({
      states: has
        ? filters.states.filter((x) => x !== s)
        : [...filters.states, s],
    });
  }
  function toggleVendor(v: (typeof ALL_VENDORS)[number]) {
    const has = filters.vendors.includes(v);
    setFilters({
      vendors: has
        ? filters.vendors.filter((x) => x !== v)
        : [...filters.vendors, v],
    });
  }

  // KPIs computed from the FULL data (not the filtered view) so the counters
  // remain stable as the user toggles chips.
  const kpis = useMemo(() => {
    const counts = { running: 0, queued: 0, failed24h: 0, success24h: 0 };
    // eslint-disable-next-line react-hooks/purity
    const cutoff = Date.now() - 24 * 3_600_000;
    for (const r of data) {
      if (r.state === "running") counts.running++;
      if (r.state === "queued") counts.queued++;
      const occurredMs = new Date(r.triggeredAt).getTime();
      if (occurredMs >= cutoff) {
        if (r.state === "failed") counts.failed24h++;
        if (r.state === "success") counts.success24h++;
      }
    }
    return counts;
  }, [data]);

  const cols = buildWorkflowColumns({ cancel, retry });
  const totalSuffixEl = `of ${total} runs`;

  if (isInitialLoading(status, data.length)) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" />
          <span className="opacity-40">/</span>
          <Skeleton className="h-3 w-18" />
        </div>

        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[680px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-4 w-[92%]" />
            <Skeleton className="h-4 w-[84%]" />
          </div>
          <Skeleton className="h-9 w-24 rounded-lg" />
        </div>

        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-4">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>

        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="h-7 w-20 rounded-full" />
              <Skeleton className="ml-auto h-8 w-24 rounded-lg" />
              <Skeleton className="h-8 w-20 rounded-lg" />
            </div>
          </CardContent>
        </Card>

        <TableSkeleton columns={WORKFLOW_TABLE_COLS} columnCount={6} rows={7} />
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Workflows</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Workflows
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Every workflow execution Opus has launched, across vendors. Cancel
            runaway runs, retry failures, and audit the timeline of changes.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void load()}
          disabled={status === "loading"}
        >
          <RefreshCw size={13} />
          Refresh
        </Button>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3.5">
        <KpiCard
          label="Running"
          value={kpis.running}
          icon={<Clock size={14} />}
          trend="flat"
          trendLabel="real-time"
        />
        <KpiCard
          label="Queued"
          value={kpis.queued}
          icon={<CalendarClock size={14} />}
          trend="flat"
          trendLabel="waiting for slot"
        />
        <KpiCard
          label="Succeeded · 24h"
          value={kpis.success24h}
          icon={<CheckCircle2 size={14} />}
          trend="up"
          trendLabel="vs prior 24h"
        />
        <KpiCard
          label="Failed · 24h"
          value={kpis.failed24h}
          icon={<XCircle size={14} />}
          trend={kpis.failed24h > 0 ? "down" : "flat"}
          trendLabel={kpis.failed24h > 0 ? "needs triage" : "clean window"}
        />
      </div>

      <WorkflowFilterBar
        filters={filters}
        toggleState={toggleState}
        toggleVendor={toggleVendor}
        setFilters={setFilters}
        resetFilters={resetFilters}
      />

      {/* No-keys empty state — a fresh user with zero runs and zero
          vendor keys gets a real CTA instead of "No runs match your
          filters." (which is misleading; they haven't filtered). */}
      {showNoKeysEmptyState ? (
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-aurora-soft text-violet">
              <KeyRound size={20} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-fg">No workflow runs yet</div>
              <div className="text-sm text-fg-dim">
                Runs are recorded when you trigger a workflow against a vendor —
                onboarding, debranding, backup/restore, compliance scans. Add a
                vendor key first, then kick off your first run from the relevant
                vendor page.
              </div>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="ghost">
                <Link to="/aruba/keys">
                  <KeyRound size={13} />
                  Aruba bundle
                </Link>
              </Button>
              <Button asChild size="sm" variant="primary">
                <Link to="/meraki/keys">
                  <KeyRound size={13} />
                  Meraki key
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* The single workflow runs table — DataTable handles search + sort + pagination. */}
      <DataTable
        columns={cols}
        data={data}
        rowKey={(r) => r.id}
        searchPlaceholder="Filter by workflow, target, user, message…"
        searchableFields={
          [
            "workflowName",
            "workflowSlug",
            "target",
            "triggeredBy",
            "message",
          ] as Array<keyof WorkflowRun>
        }
        totalSuffix={totalSuffixEl}
        emptyMessage={
          <div className="flex flex-col items-center gap-2">
            <Sparkles size={20} className="text-violet" />
            <div className="text-sm font-semibold text-fg">
              No runs match your filters.
            </div>
            <div className="text-xs text-fg-mute">
              Try widening the chips above or clearing them.
            </div>
          </div>
        }
      />
    </div>
  );
}
